import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, ArrowRight, AlertTriangle } from "lucide-react";

/**
 * Dashboard quick-ask. Silent when Pace is disabled. Shows the assistant reply
 * inline directly under the input plus a "Continue in chat →" deep link.
 */
export default function AskPaceInline({ paceEnabled }) {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState(null);        // {content, rendered_by}
  const [convId, setConvId] = useState(null);
  const [error, setError] = useState(null);        // {kind:"limit"|"other", msg}
  const inputRef = useRef(null);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/pace/ask", { question: q });
      setReply(data.reply);
      setConvId(data.conversation_id);
      setQuestion("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      if (status === 429) setError({ kind: "limit", msg: detail });
      else if (status === 503) setError({ kind: "disabled", msg: detail });
      else setError({ kind: "other", msg: detail });
    } finally {
      setLoading(false);
    }
  }, [question, loading]);

  // If we're told Pace is disabled, render nothing.
  if (!paceEnabled) return null;

  return (
    <section
      aria-label="Ask Pace"
      data-testid="ask-pace-inline"
      className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-[0_6px_22px_rgba(11,37,69,0.05)]"
    >
      <div className="flex items-center gap-3">
        <img
          src="/brand/pace/pace-avatar-64.png"
          alt=""
          className="h-8 w-8 rounded-full ring-1 ring-secondary/40"
        />
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">
            Ask Pace
          </div>
          <div className="text-sm text-muted-foreground">
            Your time, your questions. Answers cite what Pace can see.
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="flex items-center gap-2">
        <Input
          ref={inputRef}
          data-testid="ask-pace-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask Pace about your time…"
          maxLength={2000}
          disabled={loading}
          className="flex-1"
        />
        <Button
          type="submit"
          disabled={loading || !question.trim()}
          data-testid="ask-pace-submit"
          className="shrink-0"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          <span className="ml-1.5 hidden sm:inline">Ask</span>
        </Button>
      </form>

      {error && (
        <div
          data-testid="ask-pace-error"
          className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm text-primary"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div>{error.msg}</div>
        </div>
      )}

      {reply && (
        <div
          data-testid="ask-pace-answer"
          className="flex items-start gap-3 rounded-xl border border-border bg-accent/30 p-4"
        >
          <img
            src="/brand/pace/pace-avatar-128.png"
            alt="Pace"
            className="h-10 w-10 shrink-0 rounded-full ring-1 ring-secondary/40"
          />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-primary">
              {reply.content}
            </p>
            <div className="flex items-center justify-between gap-2">
              {convId ? (
                <Link
                  to={`/app/pace?c=${convId}`}
                  data-testid="ask-pace-continue"
                  className="inline-flex items-center gap-1 text-xs font-medium text-secondary underline decoration-dotted underline-offset-2 hover:text-secondary/80"
                >
                  Continue in chat
                  <ArrowRight className="h-3 w-3" />
                </Link>
              ) : <span />}
              <span
                className={
                  reply.rendered_by === "llm"
                    ? "text-[10px] uppercase tracking-widest text-secondary/80"
                    : "text-[10px] uppercase tracking-widest text-muted-foreground/70"
                }
              >
                {reply.rendered_by === "llm"
                  ? "ai"
                  : reply.rendered_by === "refusal"
                    ? "scope"
                    : "template"}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
